import { Fragment, KeyboardEvent, useRef } from 'react';

interface BarraDeAbasProps {
  /** Rótulos visíveis; `<br />` no rótulo marca a quebra de linha do design. */
  rotulos: string[];
  /** Índice VISÍVEL da aba ativa. */
  ativa: number;
  onSelecionar: (indiceVisivel: number) => void;
}

function Rotulo({ texto }: { texto: string }) {
  const linhas = texto.split(/\s*<br\s*\/?>\s*/i);

  return (
    <>
      {linhas.map((linha, indice) => (
        <Fragment key={linha}>
          {indice > 0 && <br />}
          {linha}
        </Fragment>
      ))}
    </>
  );
}

/** Painel único das abas: mostra o conteúdo da aba ativa (PERFIL-53). */
export const ID_DO_PAINEL_DAS_ABAS = 'painel-do-perfil';

export function idDaAba(indiceVisivel: number) {
  return `aba-do-perfil-${indiceVisivel}`;
}

/**
 * Abas no padrão da WAI-ARIA (PERFIL-53): só a aba ativa entra na ordem de Tab; setas
 * esquerda/direita levam à vizinha (com volta nas pontas) e Home/End às pontas, ativando
 * a aba que recebe o foco.
 */
export default function BarraDeAbas({ rotulos, ativa, onSelecionar }: BarraDeAbasProps) {
  const lista = useRef<HTMLDivElement>(null);

  function irPara(indice: number) {
    const destino = (indice + rotulos.length) % rotulos.length;
    onSelecionar(destino);
    lista.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[destino]?.focus();
  }

  function aoTeclar(evento: KeyboardEvent<HTMLDivElement>) {
    const atual = Math.max(0, ativa);
    const destinos: Record<string, number> = {
      ArrowRight: atual + 1,
      ArrowLeft: atual - 1,
      Home: 0,
      End: rotulos.length - 1,
    };
    if (!(evento.key in destinos)) return;
    evento.preventDefault();
    irPara(destinos[evento.key]);
  }

  return (
    <div
      ref={lista}
      role="tablist"
      aria-label="Seções do perfil"
      onKeyDown={aoTeclar}
      className="grid auto-cols-[minmax(140px,1fr)] grid-flow-col gap-2 overflow-x-auto overflow-y-hidden rounded-xl bg-surface-0 shadow-jj-01"
    >
      {rotulos.map((rotulo, indice) => {
        const selecionada = indice === ativa;

        return (
          <button
            key={rotulo}
            id={idDaAba(indice)}
            type="button"
            role="tab"
            aria-selected={selecionada}
            aria-controls={ID_DO_PAINEL_DAS_ABAS}
            tabIndex={selecionada ? 0 : -1}
            onClick={() => onSelecionar(indice)}
            className={`border-0 px-3 py-6 font-display text-[17px] font-bold leading-[1.2] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-brand-blue-dark ${
              selecionada ? 'bg-brand-blue text-brand-white focus-visible:outline-brand-white' : 'bg-transparent text-brand-blue-dark hover:brightness-[0.97]'
            }`}
          >
            <Rotulo texto={rotulo} />
          </button>
        );
      })}
    </div>
  );
}
